import { Permission, AdminUser } from "src/api/types";
import routes from "src/config/routes";
import { CurrencyEURIcon } from "../components/icons/currency-eur-icon";
import { CurrencyGBPIcon } from "../components/icons/currency-gbp-icon";
import { CurrencyGELIcon } from "../components/icons/currency-gel-icon";
import { CurrencyUAHIcon } from "../components/icons/currency-uah-icon";
import { CurrencyUSDIcon } from "../components/icons/currency-usd-icon";
import { CurrencyCNYIcon } from "../components/icons/currency-cny-icon";
import { CurrencyRUBIcon } from "../components/icons/currency-rub-icon";
import { APISomethingWrongMsg, FullSidebarMenus } from "./constants";
import { DateRange } from "rsuite/esm/DateRangePicker/types";
import moment from "moment";
import Cookies from "js-cookie";
import { v4 as uuidv4 } from "uuid";
import CryptoJS, { AES, enc } from "crypto-js";

import { extractSubPermission } from "src/hooks/use-permissions";

export const makeSidebarMenu = (permissions: Permission[] = []) => {
  if (!permissions || !Array.isArray(permissions)) return [];

  const isPermitted = (permName: string | string[]): boolean => {
    if (!permName) return true;
    if (Array.isArray(permName)) {
      return permName.some((item) => isPermitted(item));
    }
    const target = permName.toLowerCase().trim();
    const found: any = permissions.find((p: any) => {
      const pName = (p?.name || p?.permission_name || p?.permission_key)
        ?.toLowerCase()
        ?.trim();
      return pName === target;
    });
    if (!found) return false;
    if (
      found.can_view !== undefined ||
      found.can_list !== undefined ||
      found.subPermissions !== undefined ||
      found.sub_permissions !== undefined ||
      found.actions !== undefined
    ) {
      const canView = extractSubPermission(found, "can_view");
      const canList = extractSubPermission(found, "can_list");
      return canView || canList;
    }
    return true;
  };

  const menus = FullSidebarMenus
    .map((menu) => {
      const clonedMenu = { ...menu };

      if (clonedMenu.subMenu) {
        clonedMenu.subMenu = clonedMenu.subMenu.filter((sub) => {
          if (!sub.permission) return true;
          return isPermitted(sub.permission);
        });
      }

      return clonedMenu;
    })
    .filter((menu) => {
      // If any subMenu is configured, don't show the accordion if all sub items are restricted
      if (menu.subMenu && menu.subMenu.length === 0) {
        return false;
      }

      if (!menu.permission) return true;

      const hasDirectPermission = isPermitted(menu.permission as string | string[]);
      if (hasDirectPermission) return true;

      // If any subMenu item is allowed, keep the parent menu visible
      if (menu.subMenu && menu.subMenu.length > 0) {
        return true;
      }

      return false;
    });

  menus.sort((a, b) => a.orderId - b.orderId);

  return menus;
};

export const getFirstPermissionRoute = (user?: AdminUser | null): string => {
  if (!user) return routes.dashboard;

  const rolePermissions: Permission[] =
    user?.role?.permissions || user?.permissions || [];

  if (!Array.isArray(rolePermissions) || rolePermissions.length === 0) {
    return routes.dashboard;
  }

  const hasViewAccess = (perm: any): boolean => {
    if (!perm) return false;
    if (
      perm.can_view !== undefined ||
      perm.can_list !== undefined ||
      perm.subPermissions !== undefined ||
      perm.sub_permissions !== undefined ||
      perm.actions !== undefined
    ) {
      const canView = extractSubPermission(perm, "can_view");
      const canList = extractSubPermission(perm, "can_list");
      return canView || canList;
    }
    return true;
  };

  // 1. Try to find the route for the role's permissions in array order (1st permission first)
  for (const perm of rolePermissions) {
    if (!hasViewAccess(perm)) continue;

    const target = (
      perm?.name ||
      perm?.permission_name ||
      perm?.permission_key ||
      ""
    )
      .toLowerCase()
      .trim();

    if (!target) continue;

    // Search submenus first for exact sub-permission match
    for (const menu of FullSidebarMenus) {
      if (menu.subMenu) {
        for (const sub of menu.subMenu) {
          const subPerm = sub.permission;
          if (
            typeof subPerm === "string" &&
            subPerm.toLowerCase().trim() === target
          ) {
            const link = sub.link;
            return link.startsWith("/") ? link : `/${link}`;
          }
        }
      }
    }

    // Search top-level menus
    for (const menu of FullSidebarMenus) {
      const mPerm = menu.permission;
      const isDirectMatch =
        (typeof mPerm === "string" && mPerm.toLowerCase().trim() === target) ||
        (Array.isArray(mPerm) &&
          mPerm.some((p) => p.toLowerCase().trim() === target)) ||
        (menu.title && menu.title.toLowerCase().trim() === target);

      if (isDirectMatch) {
        if (menu.subMenu && menu.subMenu.length > 0) {
          // Find first allowed subMenu for this role if possible
          const permittedSub = menu.subMenu.find((s) => {
            if (!s.permission) return true;
            return rolePermissions.some((p: any) => {
              const pName = (p?.name || p?.permission_name || p?.permission_key)
                ?.toLowerCase()
                ?.trim();
              return (
                pName === s.permission.toLowerCase().trim() && hasViewAccess(p)
              );
            });
          });
          const link = permittedSub ? permittedSub.link : menu.subMenu[0].link;
          return link.startsWith("/") ? link : `/${link}`;
        }
        const link = menu.link;
        return link.startsWith("/") ? link : `/${link}`;
      }
    }
  }

  // 2. Fallback to makeSidebarMenu order
  const menus = makeSidebarMenu(rolePermissions);
  if (menus && menus.length > 0) {
    const firstMenu = menus[0];
    const targetLink =
      firstMenu.subMenu && firstMenu.subMenu.length > 0
        ? firstMenu.subMenu[0].link
        : firstMenu.link;
    if (targetLink) {
      return targetLink.startsWith("/") ? targetLink : `/${targetLink}`;
    }
  }

  return routes.dashboard;
};

export const getPaymentOptionName = (item: string) => {
  switch (item) {
    case "paypal":
      return "Paypal";
    case "bank_transfer":
      return "Bank";
    case "cartubank":
      return "Card";
    default:
      return "Paypal";
  }
};

export const getTransactionStatusStyle = (value: string) => {
  if (value === "" || value === "__") {
    return value;
  }
  let val = value?.toLowerCase();
  logger.log(val, "IOP_WE*E");
  switch (val) {
    case "waiting":
      return "bg-secondary text-white";
    case "completed":
    case "success":
      return "bg-active text-primary";
    case "canceled":
      return "bg-error text-white";
    default:
      return "bg-primary text-white";
  }
};

export const getStatusStyle = (value: string) => {
  switch (value) {
    case "pending":
      return "bg-secondary text-white";
    case "approved":
      return "bg-[#0b9630] text-white";
    case "rejected":
      return "bg-error text-white";
    case "identified":
      return "bg-active text-primary";
    case "unidentified":
      return "bg-secondary text-white";
    case "deactivated":
      return "bg-[#bcc20a] text-white";
    default:
      return "bg-primary text-white";
  }
};

export const getKYCStatusName = (value: string) => {
  switch (value) {
    case "pending":
      return "PENDING";
    case "approved":
      return "VERIFIED";
    case "rejected":
      return "REJECTED";
    default:
      return "NOT VERIED";
  }
};

export const getKYCStatusStyle = (value: string) => {
  switch (value) {
    case "pending":
      return "text-error font-manrope text-[14px] font-medium flex-shrink";
    case "approved":
      return "text-success font-manrope text-[14px] font-medium flex-shrink";
    case "rejected":
      return "text-error font-manrope text-[14px] font-medium flex-shrink";
    default:
      return "text-error font-manrope text-[14px] font-medium flex-shrink";
  }
};
export const getReset2faStatusName = (value: string) => {
  switch (value) {
    case "0":
      return "Reject";
    case "1":
      return "New";
    case "2":
      return "Accept";
    default:
      return "Reject";
  }
};

export const getReset2faStatusStyle = (value: string) => {
  switch (value) {
    case "0":
      return "bg-error text-white";
    case "1":
      return "bg-secondary text-white ";
    case "2":
      return "bg-active text-primary";
    default:
      return "bg-error text-white";
  }
};

export const getEmailStatusStyleName = (value: string) => {
  switch (value) {
    case "unconfirmed":
      return "bg-error text-white";
    case "confirmed":
      return "bg-active text-primary";
    case "expired":
      return "bg-secondary text-white ";
    default:
      return "bg-error text-white";
  }
};

export const capitalizeFirstLetter = (str: string): string => {
  if (str) {
    return str?.charAt(0)?.toUpperCase() + str?.slice(1);
  } else return str;
};

export const capitalizeFirstLetter_1 = (str: string): string => {
  const updatedStrs = str.split("_");
  let result = updatedStrs
    .map(
      (char: any) => char.charAt(0).toUpperCase() + char.slice(1).toLowerCase(),
    )
    .join(" ");
  return result;
};

export const extractBillingStatus = (str: string): string => {
  if (str === "" || str === "__") {
    return str;
  }
  return str?.split("|")[1];
};

export const extractBillingCategories = (str: string): string => {
  return str.toLowerCase().replace(/\s+/g, "_").trim();
};
export const capitalizeEachFirstLetter = (str: string): string => {
  const updatedStrs = str
    .split(" ")
    .map((item) => item.charAt(0).toUpperCase() + item.slice(1));
  return updatedStrs.join(" ");
};

export const splitLetettLetter = (str: string): string => {
  const updatedStrs = str?.split("_");
  return updatedStrs.join(" ");
};

// Partner Bank
// Other Banks
export const convertPaymentSystem = (str: string): string => {
  if (str === "paypal") {
    str = "PayPal";
  } else if (str == "Card") {
    str = "Card";
  } else if (str == "sepa_transfer") {
    str = "Sepa Transfer";
  } else if (str === "self_terminal") {
    str = "Self Terminal";
  } else if (str === "wallet_transfer") {
    str = "Wallet Transfer";
  } else if (str === "admin_panel") {
    str = "Admin Panel";
  } else if (str === "manual") {
    str = "Manual";
  } else if (str === "wallet_terminal") {
    str = "Wallet terminal";
  } else if (str === "wallet_terminal_history") {
    str = "Wallet terminal";
  } else if (str === "tbc_payments") {
    str = "Card";
  } else {
    str = "Bank Transfer";
  }

  const updatedStrs = str
    .split("_")
    .map((item) => item.charAt(0).toUpperCase() + item.slice(1));
  return updatedStrs.join(" ");
};

export const convertChannelName = (str: string = ""): string => {
  if (!str) return "";

  // If string contains underscore → convert normally
  if (str.includes("_")) {
    return str
      .split("_")
      .map((item) => item.charAt(0).toUpperCase() + item.slice(1))
      .join(" ");
  }

  // Else → return the string with first letter capitalized
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const formatCardNumber = (cardNumber: string) => {
  const cardLength = cardNumber.length;

  // Check if the card number length is either 15 or 16 digits
  if (cardLength >= 8) {
    // At least 8 digits are needed to show first and last four
    const firstFour = cardNumber.slice(0, 4);
    const lastFour = cardNumber.slice(cardLength - 4);
    return `${firstFour} **** **** ${lastFour}`;
  }

  return "__"; // Fallback if the card number is too short (less than 8 digits)
};

export const convertChannelNameFee = (str: string): string => {
  if (str === "bank_transfer") {
    str = "partner_bank";
  } else if (str === "bank_transfer_other") {
    str = "other_bank";
  }
  const updatedStrs = str
    ?.split("_")
    ?.map((item) => item.charAt(0).toUpperCase() + item.slice(1));
  return updatedStrs?.join(" ");
};

export const convertServiceName = (str: string): string => {
  const newStr = str
    ?.toLowerCase()
    .replace(/-/g, " ") // 1. convert hyphens to spaces
    .replace(/[^a-z0-9\s]/g, "") // 2. remove other special characters
    .trim()
    .replace(/\s+/g, "_"); // 3. replace all spaces with underscores

  return newStr;
};

export const getServiceName = (str: string): string => {
  let newStr = str
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, "") // remove special characters
    .trim()
    .replace(/\s+/g, "_") // replace spaces with underscores
    .replace(/^([a-z])/, (m) => m.toUpperCase()); // capitalize first letter
  return newStr;
};
export const extractServiceName = (str: string) => {
  let newStr = str
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/[^a-z0-9\s]/g, "")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
  return newStr;
};
function removeFalsy(obj: any) {
  return Object.fromEntries(Object.entries(obj).filter(([_, v]) => Boolean(v)));
}

export const getErrorMessage = (data: any) => {
  if (!data) return APISomethingWrongMsg;

  // Unwrap axios error or wrapped error object
  if (data?.response?.data) {
    data = data.response.data;
  }

  if (typeof data === "string") {
    return data.startsWith("error_") ? data : `error_${data}`;
  }

  if (data instanceof Error) {
    const msg = data.message || APISomethingWrongMsg;
    return msg.startsWith("error_") ? msg : `error_${msg}`;
  }

  if (data.errors) {
    const errors = data.errors;
    if (Array.isArray(errors)) {
      const messages = errors.map((error: any) =>
        typeof error === "string" ? error.replace(/_/g, " ") : JSON.stringify(error)
      );
      return messages.length > 0
        ? `error_${capitalizeFirstLetter(messages[0])}`
        : APISomethingWrongMsg;
    } else if (typeof errors === "object" && errors !== null) {
      const values = Object.values(errors) as any[];
      const messages = values.map((eachValue: any) => {
        if (Array.isArray(eachValue) && eachValue.length > 0) {
          return typeof eachValue[0] === "string"
            ? eachValue[0].replace(/_/g, " ")
            : String(eachValue[0]);
        }
        return typeof eachValue === "string"
          ? eachValue.replace(/_/g, " ")
          : String(eachValue);
      });
      return messages.length > 0
        ? `error_${capitalizeFirstLetter(messages[0])}`
        : APISomethingWrongMsg;
    }
  }

  if (data.message && typeof data.message === "string" && data.message.trim() !== "") {
    return data.message.startsWith("error_") ? data.message : `error_${data.message}`;
  }

  if (
    data.error_description &&
    typeof data.error_description === "string" &&
    data.error_description.trim() !== ""
  ) {
    return `error_${data.error_description}`;
  }

  if (data.error) {
    if (typeof data.error === "string") {
      return data.error.startsWith("error_") ? data.error : `error_${data.error}`;
    }
    if (typeof data.error === "object" && data.error?.message) {
      return `error_${data.error.message}`;
    }
  }

  if (data.msg && typeof data.msg === "string" && data.msg.trim() !== "") {
    return `error_${data.msg}`;
  }

  return APISomethingWrongMsg;
};

export function formatAddress(address: any) {
  if (!address) return;
  const temp = ["street", "city", "post_code", "country"].reduce(
    (acc, k) => ({ ...acc, [k]: (address as any)[k] }),
    {},
  );
  const formattedAddress = removeFalsy(temp);
  return Object.values(formattedAddress).length === 0
    ? "-"
    : Object.values(formattedAddress).join(", ");
}

export function formatAddressWithoutCountry(address: any) {
  if (!address) return;
  const temp = ["street", "city", "post_code"].reduce(
    (acc, k) => ({ ...acc, [k]: (address as any)[k] }),
    {},
  );
  const formattedAddress = removeFalsy(temp);
  return Object.values(formattedAddress).length === 0
    ? "-"
    : Object.values(formattedAddress).join(" ");
}

export function getCurrencyInfo(name: string, size: number = 24) {
  switch (name.toUpperCase()) {
    case "USD":
      return {
        icon: (
          <CurrencyUSDIcon
            className={`w-[${size}px] h-[${size}px] flex-shrink-0`}
          />
        ),
        descr: "United States Dollar",
      };
    case "EUR":
      return {
        icon: (
          <CurrencyEURIcon
            className={`w-[${size}px] h-[${size}px] flex-shrink-0`}
          />
        ),
        descr: "Euro",
      };
    case "GEL":
      return {
        icon: (
          <CurrencyGELIcon
            className={`w-[${size}px] h-[${size}px] flex-shrink-0`}
          />
        ),
        descr: "Georgian Lari",
      };
    case "GBP":
      return {
        icon: (
          <CurrencyGBPIcon
            className={`w-[${size}px] h-[${size}px] flex-shrink-0`}
          />
        ),
        descr: "British Pound",
      };
    case "UAH":
      return {
        icon: (
          <CurrencyUAHIcon
            className={`w-[${size}px] h-[${size}px] flex-shrink-0`}
          />
        ),
        descr: "Ukraine Hryvnia",
      };
    case "CNY":
      return {
        icon: (
          <CurrencyCNYIcon
            className={`w-[${size}px] h-[${size}px] flex-shrink-0`}
          />
        ),
        descr: "Chinese Yuan",
      };
    case "RUB":
      return {
        icon: (
          <CurrencyRUBIcon
            className={`w-[${size}px] h-[${size}px] flex-shrink-0`}
          />
        ),
        descr: "Russian Ruble",
      };
    default:
      return {
        icon: (
          <CurrencyUSDIcon
            className={`w-[${size}px] h-[${size}px] flex-shrink-0`}
          />
        ),
        descr: "United States Dollar",
      };
  }
}

export const makeSenderSecret = (value: string) => {
  const expression: RegExp = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
  if (expression.test(value)) {
    //email
    return value.replace(/(\w{3})[\w.-]+@([\w.]+\w)/, "$1***@$2");
  } else {
    //phone number
    return value.slice(0, 3) + value.slice(3).replace(/.(?=....)/g, "*");
  }
};

export const makePlaceholder = (value: string) => {
  const expression: RegExp = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
  if (expression.test(value)) {
    //email
    return "Email verification code";
  } else {
    //phone number
    return "Phone verification code";
  }
};

export const makeSecretKey = (length: number) => {
  let result = "";
  const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  const charactersLength = characters.length;
  let counter = 0;
  while (counter < length) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
    counter += 1;
  }
  return result;
};

export const makeCardSecretCardNumber = (cardNumber: string) => {
  return cardNumber
    .replace(/.(?=.{4})/g, "x")
    .match(/.{1,4}/g)!
    .join(" ");
};

export const makeNumberWithFloatingDigits = (
  value: string | number,
  digits: number = 8,
) => {
  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: digits,
  });
};

export const AuthenticatorAppURL = {
  ios: "https://apps.apple.com/ru/app/google-authenticator/id388497605",
  android:
    "https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2",
};

export const verifyCardNumber = (cardNumber: string) => {
  let sum = 0;
  let temp = 0;
  let cardNumberCopy = cardNumber;
  let checkDigit = parseInt(cardNumber.slice(-1));
  let parity = cardNumberCopy.length % 2;

  for (let i = 0; i <= cardNumberCopy.length - 2; i++) {
    if (i % 2 === parity) {
      temp = +cardNumberCopy[i] * 2;
    } else {
      temp = +cardNumberCopy[i];
    }

    if (temp > 9) {
      temp -= 9;
    }

    sum += temp;
  }

  return (sum + checkDigit) % 10 === 0;
};

export const numberFormat = (value: number, format: string) => {
  const transformNumber = new Intl.NumberFormat(format, {
    maximumFractionDigits: 2,
  }).format(value);
  return transformNumber;
};
export function getCurrencySymbol(name: string) {
  const currencySymbolsMap: Record<string, string> = {
    USD: "$", // US Dollar
    EUR: "€", // Euro
    GEL: "₾", // Georgian Lari
    GBP: "£", // British Pound
    UAH: "₴", // Ukrainian Hryvnia
    CNY: "¥", // Chinese Yuan
    RUB: "₽", // Russian Ruble
  };
  return currencySymbolsMap[name?.toUpperCase()];
}

export const fixAfterDecimal = (value: number, decimalDigit: number) => {
  let str = String(value);
  let [number, decimal] = str.split(".");
  if (!decimal) {
    decimal = "00";
  }
  if (decimal.length == 1) {
    decimal = decimal + "0";
  }
  let result = number + "." + decimal.slice(0, decimalDigit);
  return result;
};

export function setTabIndex(name: string, nIdx: number) {
  Cookies.set(name, `${nIdx}`, { expires: 1 });
}

export function removeTab(name: string) {
  Cookies.remove(name);
}

export function getTabIndex(name: string) {
  const nIdx = Cookies.get(name);
  return nIdx;
}
export const currentTimeForDataPicker = (data: DateRange) => {
  const startDate = new Date(data[0]);
  const endDate = new Date(data[1]);

  const currentTime = new Date();
  startDate.setHours(
    currentTime.getHours(),
    currentTime.getMinutes(),
    currentTime.getSeconds(),
  );
  endDate.setHours(
    currentTime.getHours(),
    currentTime.getMinutes(),
    currentTime.getSeconds(),
  );
  return [startDate, endDate];
};

// HMAC SUPPORTIVE FUNCTIONS
export const buildQueryString = (params: Record<string, any>) => {
  if (!params || typeof params !== "object") return "";
  const filtered = Object.entries(params).filter(
    ([_, v]) => v !== undefined && v !== null && v !== "",
  );
  // .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)); // sort keys for consistent order

  return filtered
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join("&");
};

export const generateHmac = (payload: any, method: string, path: string) => {
  logger.log("payload:", payload);

  const secretKey = process.env.REACT_APP_HMAC_SECRET_KEY!;
  const timestamp = Math.floor(Date.now() / 1000);

  let body = "";
  let finalPath = path;

  // ✅ For GET requests: include query params in path and use empty body
  if (method === "GET" && payload && Object.keys(payload).length > 0) {
    const queryString = buildQueryString(payload);
    finalPath = `${path}?${queryString}`;
  } else if (method !== "GET") {
    body = JSON.stringify(payload || {});
  }

  const hashedBody = CryptoJS.MD5(body).toString(CryptoJS.enc.Base64);

  const canonicalString = `${method}\n${finalPath}\n${timestamp}\n${hashedBody}`;

  const signature = CryptoJS.HmacSHA256(canonicalString, secretKey).toString(
    CryptoJS.enc.Hex,
  );

  const idempotencyKey = uuidv4();

  logger.log("🔹 HMAC DEBUG");
  logger.log("Method:", method);
  logger.log("Final Path:", finalPath);
  logger.log("Body:", body);
  logger.log("Canonical String:", canonicalString);
  logger.log("Signature:", signature);
  logger.log("🔹");

  return {
    "idempotency-key": idempotencyKey,
    "x-timestamp": timestamp.toString(),
    "x-signature": signature,
  };
};

const ENABLE_LOGS = false; // change this to false to disable all logs

export const logger = {
  log: (...args: any[]) => {
    if (ENABLE_LOGS) {
      logger.log(...args);
    }
  },
};

// export const generateHmac = (payload: any, method: string, path: string) => {
//   logger.log("hmac PAYLOAD", payload);
//   const secretKey = process.env.REACT_APP_HMAC_SECRET_KEY!;
//   logger.log("hmac secretKey", secretKey);

//   const timestamp = Math.floor(Date.now() / 1000);
//   const body = method === "GET" ? "" : JSON.stringify(payload || {});
//   logger.log("hmac body", body);

//   const hashedBody = CryptoJS.MD5(body).toString(CryptoJS.enc.Base64);
//   logger.log("hmac hashedBody", hashedBody);

//   const updatedPayload = `${method}\n${path}\n${timestamp}\n${hashedBody}`;
//   logger.log("HMAC updated payload", updatedPayload);
//   const signature = CryptoJS.HmacSHA256(updatedPayload, secretKey).toString(
//     CryptoJS.enc.Hex
//   );
//   logger.log("HMAC signature", signature);

//   const idempotencyKey = uuidv4();

//   return {
//     "idempotency-key": idempotencyKey,
//     "x-timestamp": timestamp.toString(),
//     "x-signature": signature,
//   };
// };
